import {logAnalyticsEvent} from "@/firebase/firebaseAnalytics";
import {getAuthenticatedHomeRoute} from "@/lib/HomeRedirect";
import {writeOnboardingComplete} from "@/lib/OnboardingCache";
import {
    AUTHENTICATED_HOME_ROUTE,
    type SessionRouteDecision,
} from "@/lib/SessionResolver";
import {peekSessionRouteLoopCount, recordSessionRouteNavigation} from "@/lib/SessionRouteLoop";
import {completeDesktopAppAuthHandoff} from "@/lib/DesktopAuth";
import type {SessionRouteReason} from "@/lib/SessionResolver";

export function sessionRecoverErrorKey(reason?: SessionRouteReason | string | null): string {
    if (reason === "loop_breaker") {
        return "errors.sessionLoopRecover";
    }
    if (reason === "auth_degraded") {
        return "errors.sessionAuthDegraded";
    }
    return "errors.accountSetupGeneric";
}

export type SessionRetryAction = "reload" | "retry_ensure";

/**
 * Auth-init and redirect-loop recovery stay on the same screen if retry only
 * re-runs the session effect. Those cases need a reload (after clearing a
 * loop counter). Profile-ensure failures can retry in place.
 */
export function resolveSessionRetry(input: {
    authDegraded: boolean;
    reason?: SessionRouteReason | string | null;
}): SessionRetryAction {
    if (
        input.authDegraded
        || input.reason === "auth_degraded"
        || input.reason === "loop_breaker"
    ) {
        return "reload";
    }
    return "retry_ensure";
}

type RouterLike = {
    push: (href: string) => unknown;
    replace: (href: string) => unknown;
};

export async function resolveAuthenticatedHomeRoute(uid: string): Promise<string> {
    try {
        return await getAuthenticatedHomeRoute(uid);
    } catch (error) {
        console.error("Failed to determine home redirect destination", error);
        return "/video";
    }
}

/**
 * Drop a pending entry navigation when Firebase Auth has already switched
 * users. Signup over an existing session can finish the old bootstrap after
 * `createUserWithEmailAndPassword` replaces `currentUser`.
 */
export function shouldApplySessionNavigationForUid(
    decisionUid: string | undefined,
    authenticatedUid?: string | null,
): boolean {
    if (!decisionUid || authenticatedUid === undefined) {
        return true;
    }
    return authenticatedUid === decisionUid;
}

export async function applySessionRouteDecision(input: {
    decision: SessionRouteDecision;
    uid?: string;
    router: RouterLike;
    replace?: boolean;
    source: string;
    authenticatedUid?: string | null;
}): Promise<"applied" | "skipped"> {
    const {decision, uid, router, source} = input;
    const loopCount = peekSessionRouteLoopCount();

    void logAnalyticsEvent("session_route_resolved", {
        source,
        kind: decision.kind,
        reason: decision.reason,
        route: decision.route || "",
        loop_count: loopCount,
    });

    if (decision.kind === "handoff") {
        await completeDesktopAppAuthHandoff();
        return "applied";
    }

    if (decision.kind !== "navigate" || !decision.route) {
        return "skipped";
    }

    if (!shouldApplySessionNavigationForUid(uid, input.authenticatedUid)) {
        return "skipped";
    }

    let route = decision.route;
    if (route === AUTHENTICATED_HOME_ROUTE) {
        if (!uid) {
            return "skipped";
        }
        if (
            decision.reason === "onboarding_complete"
            || decision.reason === "onboarding_complete_cached"
        ) {
            writeOnboardingComplete(uid);
        }
        route = await resolveAuthenticatedHomeRoute(uid);
    }

    recordSessionRouteNavigation(route);
    if (input.replace) {
        router.replace(route);
    } else {
        router.push(route);
    }
    return "applied";
}
